import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { useHelp } from "../context/HelpContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

const FALLBACK_PHONE = "33664248707";

function defaultMessageForPath(pathname, siteName) {
  const sn = siteName || "SF Learning";
  if (pathname === "/") return `Bonjour ! J'aimerais en savoir plus sur ${sn}.`;
  if (pathname.startsWith("/catalog")) return `Bonjour ! Je consulte le catalogue ${sn} et j'ai une question.`;
  if (pathname.startsWith("/courses/")) return `Bonjour ! J'ai une question sur l'une de vos formations.`;
  if (pathname.startsWith("/packs/")) return `Bonjour ! J'ai une question sur l'un de vos packs.`;
  if (pathname.startsWith("/checkout")) return `Bonjour ! J'ai besoin d'aide pour finaliser ma commande sur ${sn}.`;
  if (pathname.startsWith("/learn/")) return `Bonjour ! J'ai une question sur une leçon ${sn}.`;
  if (pathname.startsWith("/affiliate")) return `Bonjour ! J'ai une question sur le programme de parrainage ${sn}.`;
  if (pathname.startsWith("/community")) return `Bonjour ! J'ai une question sur la communauté ${sn}.`;
  return `Bonjour ! J'ai une question concernant ${sn}.`;
}

export default function WhatsAppHelp() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const location = useLocation();
  const { pageHelp } = useHelp() || {};
  const { user } = useAuth() || {};
  const { settings } = useSettings() || {};

  const phone = (settings?.whatsapp_number || FALLBACK_PHONE).replace(/[^0-9]/g, "");
  const baseMessage = pageHelp?.message || defaultMessageForPath(location.pathname, settings?.site_name);
  const finalMessage = (text.trim() ? `${baseMessage}\n\n${text.trim()}` : baseMessage)
    + (user?.email ? `\n\n— ${user.name || ""} (${user.email})` : "");

  const open_whatsapp = () => {
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(finalMessage)}`;
    // NE PAS passer par le navigateur integre ici : dans l'app, window.open
    // ouvre directement WhatsApp, ce qui est le comportement voulu (une etape
    // de moins pour l'utilisateur qu'un passage par une page web).
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
    setText("");
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed z-50 bottom-6 right-6 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-elev hover:scale-105 transition-transform flex items-center justify-center"
        aria-label="Demander de l'aide via WhatsApp"
        data-testid="whatsapp-help-fab"
        title="Demander de l'aide"
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : (
          <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor" aria-hidden="true">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 01-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 01-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.5-1.408a3.18 3.18 0 00.158-.917c0-.273-.187-.387-.429-.508-.515-.272-1.604-.776-2.215-.99zM16.072 0C7.243 0 0 7.243 0 16.072c0 3.013.832 5.937 2.4 8.464L.014 32l7.713-2.4a16.09 16.09 0 008.345 2.343c8.83 0 16.072-7.243 16.072-16.072S24.9 0 16.072 0zm0 28.945a13.32 13.32 0 01-7.346-2.214l-.515-.33-5.327 1.66 1.69-5.198-.33-.516a13.18 13.18 0 01-2.05-7.114c0-7.305 5.94-13.244 13.244-13.244 7.308 0 13.244 5.94 13.244 13.244 0 7.308-5.94 13.244-13.244 13.244z" />
          </svg>
        )}
      </button>

      {/* Popover */}
      {open && (
        <div
          className="fixed z-50 bottom-24 right-6 w-[340px] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-elev border border-emerald-100 overflow-hidden animate-fade-up"
          data-testid="whatsapp-help-popover"
        >
          <div className="bg-[#075E54] text-white p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Support {settings?.site_name || "SF Learning"}</p>
                <p className="text-xs text-white/80">Réponse rapide via WhatsApp</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-[#f0e8de]">
            <div className="bg-white rounded-lg p-3 shadow-sm text-sm text-gray-700 mb-3">
              👋 Bonjour ! Une question sur nos formations, ton inscription, un paiement ou une leçon ?
              <br /><br />
              Décris ta question ci-dessous, on te répond directement sur WhatsApp.
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Ta question (facultatif)..."
              className="w-full text-sm p-2 rounded-lg border border-gray-300 focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 outline-none resize-none"
              data-testid="whatsapp-help-input"
            />
            <button
              onClick={open_whatsapp}
              className="w-full mt-3 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-2.5 rounded-full inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="whatsapp-help-send-btn"
            >
              <Send className="w-4 h-4" /> Ouvrir WhatsApp
            </button>
            <p className="text-xs text-gray-500 mt-2 text-center">Le bouton ouvrira WhatsApp avec un message pré-rempli.</p>
          </div>
        </div>
      )}
    </>
  );
}
